import React from 'react';

import OrangeSpinner from '../../components/loading/OrangeSpinner';
import { TINTA_GRAFICO } from './chart-palette';

/**
 * Peças de montagem dos gráficos do Dashboard da Associação (at-86).
 *
 * Existem para que as regras de leitura não dependam de alguém lembrar delas em cada gráfico:
 * o cartão sempre explica o que está mostrando, a legenda sempre aparece quando há mais de uma
 * série, e o estado vazio nunca fala por conta própria (quem decide se "não há dado" pode ser dito
 * é a tela, que sabe se a consulta respondeu).
 */

interface KpiProps {
  rotulo: string;
  valor: string;
  /** Linha pequena embaixo: a leitura de apoio que impede o número de ser mal interpretado. */
  apoio?: string;
  /**
   * A ajuda embutida: **o que o indicador mede**, numa frase (at-117).
   *
   * 🔴 Diferente de `apoio`, que descreve o VALOR ("mediana · 90% em até 36 d"). Esta descreve a
   * DEFINIÇÃO — o que entra na conta —, e existe para o indicador cujo rótulo não se explica
   * sozinho. O caso que a criou: "Tempo na fila de Compras", em que o leitor supõe que o número é
   * o tempo até a peça chegar, quando é o tempo até a compra sair.
   *
   * ⚠️ Uma frase curta. O card é estreito e divide a fileira com outros cinco — explicação longa
   * é trabalho do drawer "Como Funciona".
   */
  hint?: string;
  /** Proporção 0..100. Presente, desenha o medidor — usado na adimplência. */
  proporcao?: number;
  /**
   * Consulta em andamento e ainda sem número para mostrar.
   *
   * ⚠️ Enquanto carrega, o indicador mostra o girador NO LUGAR do valor — nunca um traço ou um
   * zero. Traço é indistinguível de "consultei e não há", que é justamente a confusão que a
   * at-60 existe para evitar; e zero seria pior, porque é um número plausível.
   */
  carregando?: boolean;
  /**
   * O número NÃO EXISTE sob os filtros em vigor, e este texto diz por quê (at-115).
   *
   * 🔴 Não é o mesmo que zero, e é por isso que é uma prop própria: o caso que a criou é
   * "Aguardando atendimento" com um comprador escolhido — o card conta quem **não tem** comprador,
   * então a interseção é vazia por construção. Um `0` ali se lê como "a fila está limpa".
   *
   * ⚠️ Presente, o indicador **não vira botão**: não há lista a abrir, e um clique que abre uma
   * tela vazia é pior que a ausência do clique.
   */
  indisponivel?: string;
  /**
   * Presente, o indicador vira botão e abre o detalhamento.
   *
   * ⚠️ Vira `<button>` de verdade, não um `<div>` com `onClick`: quem navega por teclado precisa
   * alcançá-lo, e leitor de tela precisa anunciar que aquilo abre algo.
   */
  onDetalhar?: () => void;
  /** O que o clique abre — vai para o `title` e para o rótulo acessível. */
  detalheRotulo?: string;
}

export const Kpi: React.FC<KpiProps> = ({
  rotulo,
  valor,
  apoio,
  hint,
  proporcao,
  carregando,
  indisponivel,
  onDetalhar,
  detalheRotulo,
}) => {
  const conteudo = (
    <KpiConteudo {...{ rotulo, valor, apoio, hint, proporcao, carregando, indisponivel }} />
  );

  if (onDetalhar && !carregando && !indisponivel) {
    return (
      <button
        type="button"
        className="do-kpi do-kpi-clicavel"
        onClick={onDetalhar}
        title={detalheRotulo ?? `Ver os atendimentos de ${rotulo}`}
        aria-label={detalheRotulo ?? `Ver os atendimentos de ${rotulo}`}
      >
        {conteudo}
      </button>
    );
  }

  return <div className="do-kpi">{conteudo}</div>;
};

const KpiConteudo: React.FC<KpiProps> = ({
  rotulo,
  valor,
  apoio,
  hint,
  proporcao,
  carregando,
  indisponivel,
}) => (
  <>
    <div className="do-kpi-rotulo">{rotulo}</div>
    {carregando ? (
      <div className="do-kpi-valor do-kpi-carregando">
        <OrangeSpinner inline pequeno rotulo={`Carregando ${rotulo}`} />
      </div>
    ) : indisponivel ? (
      // O lugar do número recebe a explicação — e não um traço, que seria indistinguível de
      // "consultei e não há" (at-60), nem um zero, que seria um número plausível e errado.
      <div className="do-kpi-indisponivel">{indisponivel}</div>
    ) : (
      <div className="do-kpi-valor">{valor}</div>
    )}
    {proporcao !== undefined && !carregando && !indisponivel && (
      <div
        className="do-medidor"
        role="img"
        aria-label={`${rotulo}: ${valor}`}
      >
        <div
          className="do-medidor-preenchimento"
          style={{ width: `${Math.max(0, Math.min(100, proporcao))}%` }}
        />
      </div>
    )}
    {apoio && <div className="do-kpi-apoio">{apoio}</div>}
    {/*
      A hint aparece mesmo CARREGANDO e mesmo INDISPONÍVEL, ao contrário do apoio: ela não fala do
      valor, fala do que o indicador mede — e é justamente quando não há número que alguém para
      para ler o que aquilo significa.
    */}
    {hint && <div className="do-kpi-hint">{hint}</div>}
  </>
);

interface CardProps {
  titulo: string;
  /**
   * O que o gráfico mostra e de onde vem — a regra, não o clique.
   *
   * ⚠️ **Opcional**: card cujo título já diz tudo dispensa a linha (pedido do Victor, 21/09/2026,
   * sobre dois cards do Dashboard de Compras). Quando ausente, o parágrafo não é renderizado — uma
   * string vazia deixaria o espaçamento dele na tela.
   */
  descricao?: string;
  children: React.ReactNode;
}

export const CardGrafico: React.FC<CardProps> = ({ titulo, descricao, children }) => (
  <div className="do-card">
    <h3 className="do-card-titulo">{titulo}</h3>
    {descricao ? <p className="do-card-descricao">{descricao}</p> : null}
    {children}
  </div>
);

interface LegendaProps {
  itens: Array<{ rotulo: string; cor: string }>;
}

/**
 * Legenda própria, em vez da `<Legend>` do recharts.
 *
 * Duas razões: o texto fica em tinta de texto (nunca na cor da série — cor clara é ilegível como
 * texto), e o marcador colorido ao lado é que carrega a identidade. Além disso a legenda fica
 * FORA da área de plotagem, então não rouba largura do gráfico no celular.
 */
export const Legenda: React.FC<LegendaProps> = ({ itens }) => (
  <div className="do-legenda">
    {itens.map((item) => (
      <div key={item.rotulo} className="do-legenda-item">
        <span className="do-legenda-marca" style={{ backgroundColor: item.cor }} aria-hidden="true" />
        {item.rotulo}
      </div>
    ))}
  </div>
);

/**
 * Mensagem dentro da área do gráfico.
 *
 * ⚠️ O TEXTO vem de quem chama, de propósito. "Sem dados no período" é afirmação de negócio e só
 * pode ser dita quando a consulta respondeu; quando ela falhou, a tela manda outro texto. Um
 * componente que decidisse a frase sozinho reintroduziria exatamente a confusão da at-60.
 */
export const AreaVazia: React.FC<{ mensagem: string }> = ({ mensagem }) => (
  <div className="do-vazio">{mensagem}</div>
);

/**
 * Área do gráfico enquanto a consulta não respondeu.
 *
 * Ocupa a MESMA caixa do gráfico e do estado vazio, para o cartão não mudar de altura quando o
 * dado chega — layout que salta faz o usuário perder o que estava lendo ao lado.
 */
export const AreaCarregando: React.FC<{ rotulo?: string }> = ({ rotulo = 'Carregando gráfico' }) => (
  <div className="do-vazio">
    <OrangeSpinner inline rotulo={rotulo} />
  </div>
);

/**
 * Girador enquanto carrega; a mensagem de quem chama quando terminou sem dado.
 *
 * A frase continua vindo de fora (ver `AreaVazia`): o componente decide apenas ENTRE os dois
 * estados, nunca o que dizer sobre a ausência de dado.
 */
export const AreaEstado: React.FC<{ carregando: boolean; mensagem: string }> = ({
  carregando,
  mensagem,
}) => (carregando ? <AreaCarregando /> : <AreaVazia mensagem={mensagem} />);

/** Eixo e grade recessivos, iguais em todos os gráficos da tela. */
export const EIXO_TICK = { fontSize: 12, fill: TINTA_GRAFICO.rotulo } as const;
export const EIXO_LINHA = { stroke: TINTA_GRAFICO.eixo } as const;
export const GRADE = { stroke: TINTA_GRAFICO.grade, strokeDasharray: '' } as const;

/** Caixa do tooltip — mesma moldura do resto da tela. */
export const ESTILO_TOOLTIP = {
  borderRadius: 10,
  border: `1px solid ${TINTA_GRAFICO.grade}`,
  fontSize: 13,
} as const;
